import { inject, injectable } from 'inversify'
import type { Factory } from '@/types'
import type { FilterInterface } from '@/services/forms/types'
import type { LoggerChannels } from '@/config/configDev'
import { TmEntityNotFoundError } from '@/core/error/tmEntityNotFoundError'
import { SERVICE_TYPES } from '@/core/container/types'
import type LoggerService from '@/services/loggerService'
import type { PaginationUrlFilterNullableType, PaginationUrlFilterType } from '@/services/tables/types'
import type { FilterSelectedItems } from '@/data/models/domain/filteredViews/types'

@injectable()
export class FilterServiceManager {
  protected readonly loggerChannel: LoggerChannels = 'filters'

  protected services: Map<string, FilterInterface> = new Map()

  private readonly managerId = 'FilterServiceManager'

  constructor(@inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService) {}

  public getAllFilterServicesForServiceId(serviceId: string): FilterInterface[] {
    return this.getAllFilterServiceIdsByServiceId(serviceId).map(
      (filterServiceId) => this.services.get(filterServiceId) as FilterInterface,
    )
  }

  public getFilterServiceForServiceId(serviceId: string, fsKey: string): FilterInterface {
    const fsId = this.getFilterServiceId(serviceId, fsKey)
    if (this.services.has(fsId)) {
      this.log(`Service "${fsId}" exists. Returning its instance.`, 'getFilterServiceForServiceId')
      return this.services.get(fsId) as FilterInterface
    }

    this.log(`Service "${fsId}" does not exists`, 'getFilterServiceForServiceId')
    throw new TmEntityNotFoundError(`Service "${fsId}" does not exists`)
  }

  public getFilterServiceForServiceIdOrNull(serviceId: string, fsKey: string): FilterInterface | null {
    try {
      return this.getFilterServiceForServiceId(serviceId, fsKey)
    } catch (e) {
      if (e instanceof TmEntityNotFoundError) {
        return null
      }

      throw e
    }
  }

  public getFirstFilterServiceForServiceId(serviceId: string): FilterInterface {
    const filterServiceIds = this.getAllFilterServiceIdsByServiceId(serviceId)
    if (!filterServiceIds.length) {
      throw new TmEntityNotFoundError(`No filter services for service "${serviceId}"`)
    }

    if (!this.services.has(filterServiceIds[0])) {
      throw new TmEntityNotFoundError(`Service "${filterServiceIds[0]}" does not exists`)
    }

    return this.services.get(filterServiceIds[0])!
  }

  public getFilterServiceById(fsKey: string) {
    if (!this.services.has(fsKey)) {
      throw new TmEntityNotFoundError(`Service "${fsKey}" does not exists`)
    }

    return this.services.get(fsKey)! // TODO: Get rid of type assertion after upgrade to TypeScript 5.5+
  }

  public resetAllFilterServicesForServiceId(serviceId: string, toSkip: string[] = []) {
    this.getAllFilterServicesForServiceId(serviceId).forEach((fs) => {
      fs.reset(toSkip)
    })
  }

  public addFilterServiceForServiceId(serviceId: string, filterFactory: Factory<FilterInterface>) {
    this.log(`Add new service "${serviceId}"`, 'addServiceForServiceId')
    const instance = filterFactory()

    const nextIndex = this.getNextFilterServiceIndexByServiceId(serviceId)
    const filterServiceId = this.getFilterServiceId(serviceId, nextIndex)

    instance.setId(filterServiceId)
    this.services.set(filterServiceId, instance)

    instance.initForm()
    instance.resetAllFilterVisibility()

    return {
      serviceId: filterServiceId,
      instance,
    }
  }

  public removeFilterServiceForServiceId(serviceId: string, fsKey: string) {
    const fsId = this.getFilterServiceId(serviceId, fsKey)
    this.log(`Removing "${fsId}" filter service`, 'removeFilterServiceForServiceId')

    const instance = this.getFilterServiceForServiceIdOrNull(serviceId, fsKey)
    if (instance) {
      instance.getForm().destroy()
      instance.destroy()
    }
    this.services.delete(fsId)
  }

  public removeAllFilterServicesForServiceId(serviceId: string) {
    this.log(`Removing filter services for service "${serviceId}"`, 'removeAllFilterServicesForServiceId')
    this.getAllFilterServiceIdsByServiceId(serviceId).forEach((filterServiceId) => {
      const instance = this.services.get(filterServiceId)
      if (instance) {
        instance.getForm().destroy()
        instance.destroy()
      }
      this.services.delete(filterServiceId)
    })
  }

  public removeFirstFilterServiceForServiceId(serviceId: string) {
    this.log(`Removing first filter service for service "${serviceId}"`, 'removeFirstFilterServiceForServiceId')
    const filterServiceIds = this.getAllFilterServiceIdsByServiceId(serviceId)
    if (!filterServiceIds.length) {
      throw new TmEntityNotFoundError(`No filter services for service "${serviceId}"`)
    }

    const instance = this.services.get(filterServiceIds[0])
    if (instance) {
      instance.getForm().destroy()
      instance.destroy()
    }
    this.services.delete(filterServiceIds[0])
  }

  public hasFilterServicesForServiceId(serviceId: string): boolean {
    return this.getAllFilterServiceIdsByServiceId(serviceId).length > 0
  }

  public hasFilterServiceForServiceId(serviceId: string, serviceKey: string): boolean {
    return this.services.has(this.getFilterServiceId(serviceId, serviceKey))
  }

  public cleanup() {
    this.log(`Cleanup`, 'cleanup')
    this.services.forEach((instance) => {
      instance.getForm().destroy()
      instance.destroy()
    })
    this.services.clear()
  }

  public parseFilterServiceId(filterServiceId: string) {
    const [, serviceId, index] = filterServiceId.split('.')
    return {
      serviceId,
      index: Number(index),
    }
  }

  public getQuery(serviceId: string, exportHidden = true): PaginationUrlFilterType {
    const services = this.getAllFilterServicesForServiceId(serviceId)
    return services.map((fs) => fs.toQuery(exportHidden)).filter((fs) => Object.keys(fs).length)
  }

  public populateFromUrl(
    serviceId: string,
    filterFactory: Factory<FilterInterface>,
    params: PaginationUrlFilterNullableType,
    selectedItems: FilterSelectedItems[] = [],
    setInitial = false,
  ): void {
    this.log(`Populate "${serviceId}" with following params: ${JSON.stringify(params)}`)

    const existing = Object.keys(this.getAllFilterServicesForServiceId(serviceId))

    // Remove excessive filter services
    for (let i = existing.length; i > params.length; i--) {
      const fsKey = String(i - 1)
      this.removeFilterServiceForServiceId(serviceId, fsKey)
    }

    // Populate existing filter services
    params.forEach((param, index) => {
      const fsInstance = existing[index]
        ? this.getFilterServiceForServiceId(serviceId, existing[index])
        : this.addFilterServiceForServiceId(serviceId, filterFactory).instance

      fsInstance.populateFromUrl(param, selectedItems, setInitial)
    })
  }

  protected getNextFilterServiceIndexByServiceId(serviceId: string): string {
    const filterServiceIds = this.getAllFilterServiceIdsByServiceId(serviceId)
    if (filterServiceIds.length) {
      const lastFilterServiceId = filterServiceIds[filterServiceIds.length - 1]
      const { index } = this.parseFilterServiceId(lastFilterServiceId)
      return String(index + 1)
    }

    return '0'
  }

  protected getAllFilterServiceIdsByServiceId(serviceId: string): string[] {
    return Array.from(this.services.keys()).filter((key) => key.startsWith(`${this.managerId}.${serviceId}.`))
  }

  protected getFilterServiceId(serviceId: string, fsKey: string) {
    return `${this.managerId}.${serviceId}.${fsKey}`
  }

  protected log(message: string, methodName?: string) {
    const subchannel = methodName ? `manager:${methodName}` : 'manager'

    if (this.loggerService.shouldLogByChannel(this.loggerChannel, [subchannel])) {
      this.loggerService.log(this.loggerChannel, message, subchannel)
    }
  }
}
